import { useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Button, DashboardModule, StatusBadge, Table, Text,
} from '@domain-group/suit-theme'

import type { EntitiesState } from '@redux/entities'

import useSelector from '@hooks/useSelector'

import type { PublicationPaginationRuleModel } from '@models/types'

import usePublicationPaginationRule from '@hooks/usePublicationPaginationRule'
import usePublicationPaginationRules from '@hooks/usePublicationPaginationRules'

import PublicationPaginationRuleActions from './PublicationPaginationRuleActions'

const buildColumns = (brands: EntitiesState['brands'], paginationRules: EntitiesState['paginationRules']) => [
  {
    title: 'Brand',
    cellProps: {
      key: 'brand',
      left: 0,
      position: 'sticky',
    },
    dataKey: 'pagination_rule_id',
    render: (pagination_rule_id: number) => {
      const paginationRule = paginationRules[pagination_rule_id] || {}
      const { title: brandTitle } = brands[paginationRule.brand_id] || {}

      return brandTitle
    },
  },
  {
    title: 'Title',
    cellProps: {
      key: 'title',
    },
    dataKey: 'pagination_rule_id',
    render: (pagination_rule_id: number) => {
      const { title: paginationRuleTitle } = paginationRules[pagination_rule_id] || {}

      return paginationRuleTitle
    },
  },
  {
    title: 'Group By',
    cellProps: {
      key: 'groupItemsBy',
    },
    dataKey: 'pagination_rule_id',
    render: (pagination_rule_id: number) => {
      const paginationRule = paginationRules[pagination_rule_id] || {}

      return paginationRule.group_items_by || 'Don\'t Group'
    },
  },
  {
    title: 'Direction',
    cellProps: {
      key: 'placementDirection',
      width: 100,
    },
    dataKey: 'pagination_rule_id',
    render: (pagination_rule_id: number) => {
      const paginationRule = paginationRules[pagination_rule_id] || {}

      return paginationRule.placement_direction || 'LTR'
    },
  },
  {
    title: 'Start',
    cellProps: {
      key: 'startPage',
      width: 100,
    },
    dataKey: 'pagination_rule_id',
    render: (pagination_rule_id: number) => {
      const paginationRule = paginationRules[pagination_rule_id] || {}

      return (
        <Text variant="ellipsis">
          {paginationRule.start_page || 'Default'}
        </Text>
      )
    },
  },
  {
    title: 'Sort Items',
    cellProps: {
      key: 'sortItemsBy',
      width: 100,
    },
    dataKey: 'pagination_rule_id',
    render: (pagination_rule_id: number) => {
      const paginationRule = paginationRules[pagination_rule_id] || {}
      const sortItemsCount = paginationRule.sort_items_by?.length || 0
      const sortItemsText = sortItemsCount === 1 ? `${sortItemsCount} sort` : `${sortItemsCount} sorts`

      return (
        sortItemsCount > 0 ? sortItemsText : 'No Rules'
      )
    },
  },
  {
    title: 'Default',
    cellProps: {
      key: 'default',
      textAlign: 'center',
    },
    dataKey: 'pagination_rule_id',
    render: (pagination_rule_id: number) => {
      const paginationRule = paginationRules[pagination_rule_id] || {}

      return (
        <StatusBadge
          boxProps={{
            width: '45px',
          }}
          key={paginationRule.id}
          color={paginationRule.default ? 'green' : 'orange'}
          ghost
          text={paginationRule.default ? 'Yes' : 'No'}
        />
      )
    },
  },
  {
    title: 'Actions',
    cellProps: {
      textAlign: 'center',
      width: 80,
    },
    dataKey: 'id',
    // @ts-expect-error TS7006
    render: (_, publicationPaginationRule: PublicationPaginationRuleModel) => (
      <PublicationPaginationRuleActions publicationPaginationRule={publicationPaginationRule} />
    ),
  },
]

const PublicationPaginationRules = (props: { publicationId: number }) => {
  const { publicationId } = props

  const publicationPaginationRulesPayload = usePublicationPaginationRules({
    filters: { publication_id: publicationId },
    performHttpRequests: !!publicationId,
    requestOptions: {
      include_publication_pagination_rule_pagination_rule: true,
      include_pagination_rule_brand: true,
    },
    // @ts-expect-error TS2559
    sortKey: 'brands',
  })

  const {
    filteredPublicationPaginationRules,
    hasFilteredPublicationPaginationRules,
    loading,
    publicationPaginationRulesUpdatedAt,
  } = publicationPaginationRulesPayload

  const publicationPaginationRulePayload = usePublicationPaginationRule({ publication_id: publicationId })
  const {
    callbacks: {
      addPublicationPaginationRule,
    },
  } = publicationPaginationRulePayload

  const { brands, paginationRules } = useSelector(reduxState => reduxState.entities)

  const memoColumns = useMemo(
    () => buildColumns(brands, paginationRules),
    [publicationPaginationRulesUpdatedAt],
  )

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-publication-rules' }}
      contentBoxProps={{ flexDirection: 'column' }}
      loading={loading}
      title="Pagination Rules"
    >
      {!loading && hasFilteredPublicationPaginationRules && (
        <Table
          boxProps={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}
          // @ts-expect-error TS2322
          columns={memoColumns}
          data={filteredPublicationPaginationRules}
          scroll={{ x: 800 }}
          stickyHeader
        />
      )}

      {!loading && !hasFilteredPublicationPaginationRules && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Pagination Rules Present
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please add a rule below
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      <DashboardModule.Footer
        boxProps={{
          borderTop: loading ? 'none' : '1px solid',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginLeft="auto"
          onClick={() => addPublicationPaginationRule()}
          // @ts-expect-error TS2322
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Rule
        </Button>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default PublicationPaginationRules
